import React, { useState, useEffect } from 'react';
import {
  Box,
  Switch,
  Text,
  HStack,
  Button,
  VStack,
  useColorMode,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faBellSlash, faVial } from '@fortawesome/free-solid-svg-icons';
import { notificationUtils } from '../utils/notifications';

const NotificationSettings = ({ permission, enabled, onToggle, onClose }) => {
  const { colorMode } = useColorMode();
  const [showSuccess, setShowSuccess] = useState(false);
  const [testStatus, setTestStatus] = useState(null); // null, 'sending', 'success', 'error'

  const handleEnableNotifications = async () => {
    const granted = await notificationUtils.requestPermission();
    
    if (granted) {
      onToggle(true);
      localStorage.setItem('blockNotificationsEnabled', 'true');
      setShowSuccess(true);
      
      // Auto-close modal after 30 seconds
      setTimeout(() => {
        if (onClose) onClose();
      }, 30000);
    }
  };

  const handleToggle = (checked) => {
    onToggle(checked);
    localStorage.setItem('blockNotificationsEnabled', checked ? 'true' : 'false');
    
    if (checked) {
      setShowSuccess(true);
      // Auto-close modal after 30 seconds
      setTimeout(() => {
        if (onClose) onClose();
      }, 30000);
    } else {
      // Reset success message when disabled
      setShowSuccess(false);
    }
  };

  // Hide success message when component unmounts or permission changes
  useEffect(() => {
    if (permission !== 'granted') {
      setShowSuccess(false);
    }
  }, [permission]);

  const handleTestNotification = async () => {
    setTestStatus('sending');
    
    try {
      // Try service worker notification first
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification('Bitcoin Block 999,999', {
        body: 'Test notification from TimechainStats',
        icon: '/favicon-192x192.png',
        tag: 'test-notification',
        requireInteraction: false,
        silent: false
      });
      
      setTestStatus('success');
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setTestStatus(null);
      }, 3000);
    } catch (error) {
      // Fallback to basic notification
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Bitcoin Block 999,999', {
            body: 'Test notification from TimechainStats',
            icon: '/favicon-192x192.png',
            requireInteraction: false
          });
          
          setTestStatus('success');
        } else {
          setTestStatus('error');
        }
      } catch (fallbackError) {
        setTestStatus('error');
      }
      
      setTimeout(() => {
        setTestStatus(null);
      }, 3000);
    }
  };

  if (!notificationUtils.isSupported()) {
    return null;
  }

  return (
    <Box 
      p={4} 
      borderRadius="3px" 
      bg={colorMode === 'dark' ? 'primary.300' : 'white'}
    >
      <VStack align="stretch" spacing={0}>
        <HStack justify="space-between">
          <HStack>
            <FontAwesomeIcon 
              icon={enabled ? faBell : faBellSlash} 
              color={enabled ? '#f7931a' : colorMode === 'dark' ? '#777777' : '#999999'}
            />
            <Text fontWeight="bold" color={colorMode === 'dark' ? 'gray.100' : 'gray.800'}>Block Alerts</Text>
          </HStack>
          
          {permission === 'granted' ? (
            <Switch
              isChecked={enabled}
              onChange={(e) => handleToggle(e.target.checked)}
              colorScheme="orange"
            />
          ) : (
            <Button
              size="sm"
              onClick={handleEnableNotifications}
              isDisabled={permission === 'denied'}
              bg="transparent"
              color="orange.500"
              borderWidth="2px"
              borderColor="orange.500"
              _hover={{ bg: 'orange.500', color: 'white' }}
              transition="all 0.2s"
            >
              {permission === 'denied' ? 'Blocked' : 'Enable'}
            </Button>
          )}
        </HStack>
        
        <Text pb={1} fontSize="sm" color={colorMode === 'dark' ? 'gray.400' : 'gray.600'}>
          {permission === 'granted' && enabled
            ? 'Receive alerts when new Bitcoin blocks are found'
            : permission === 'denied'
            ? 'Alerts are blocked. Enable them in browser settings.'
            : 'Enable notifications to know when new blocks are found'}
        </Text>

        {/* Test notification button - disabled for now */}
        {false && permission === 'granted' && enabled && (
          <Button
            size="sm"
            onClick={handleTestNotification}
            bg="transparent"
            color="orange.500"
            borderWidth="2px"
            borderColor="orange.500"
            _hover={{ bg: 'orange.500', color: 'white' }}
            transition="all 0.2s"
            leftIcon={<FontAwesomeIcon icon={faVial} />}
            width="full"
            mt={2}
            isLoading={testStatus === 'sending'}
            loadingText="Sending..."
          >
            Test Notification
          </Button>
        )}

        {testStatus === 'success' && (
          <Box 
            mt={2} 
            p={2} 
            bg="orange.900" 
            borderRadius="3px" 
            borderWidth="1px" 
            borderColor="orange.500"
          >
            <Text fontSize="xs" color="orange.200">
              ✓ Test notification sent! Check your notification panel.
            </Text>
          </Box>
        )}

        {testStatus === 'error' && (
          <Box 
            mt={2} 
            p={2} 
            bg="red.900" 
            borderRadius="3px" 
            borderWidth="1px" 
            borderColor="red.500"
          >
            <Text fontSize="xs" color="red.200">
              ✗ Notification failed. Check browser settings.
            </Text>
          </Box>
        )}

        {/* Troubleshooting box - disabled for now */}
        {false && permission === 'granted' && enabled && (
          <Box 
            mt={2} 
            p={2} 
            bg="gray.800" 
            borderRadius="3px" 
            borderWidth="1px" 
            borderColor="gray.600"
          >
            <VStack align="start" spacing={1}>
              <Text fontSize="xs" color="gray.400" fontWeight="bold">
                Troubleshooting:
              </Text>
              <Text fontSize="xs" color="gray.500">
                • Check macOS System Preferences → Notifications → Chrome
              </Text>
              <Text fontSize="xs" color="gray.500">
                • Ensure Do Not Disturb/Focus mode is OFF
              </Text>
              <Text fontSize="xs" color="gray.500">
                • Try Chrome Incognito mode or restart Chrome
              </Text>
              <Text fontSize="xs" color="gray.500">
                • Visit chrome://settings/content/notifications
              </Text>
            </VStack>
          </Box>
        )}
        
        {showSuccess && (
          <Box 
            mt={3} 
            p={3} 
            bg="orange.900" 
            borderRadius="3px" 
            borderWidth="2px" 
            borderColor="orange.500"
          >
            <Text fontSize="sm" color="orange.200" fontWeight="medium">
              ✓ Blocks alerts enabled!
            </Text>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default NotificationSettings;